'use client'

import { useCallback, useEffect, useState } from 'react'
import type { RefObject } from 'react'

export interface UseCarouselProgressResult {
  /** `true` si el contenido desborda el ancho visible del carril (hay algo
   * que desplazar). `false` ⇒ el consumidor debe ocultar la barra entera. */
  readonly hasOverflow: boolean
  /** Ancho del "thumb" de la barra, en % del ancho total del carril —
   * proporción del contenido visible sobre el total. */
  readonly thumbWidth: number
  /** Posición del "thumb", en % — proporcional al progreso del scroll. */
  readonly thumbLeft: number
  /** Recalcula `hasOverflow`/`thumbWidth`/`thumbLeft`. Conectar al `onScroll`
   * del mismo carril que usa `useRailScroll` (junto a su propio `recalc`). */
  readonly recalc: () => void
}

/**
 * Barra de progreso continua de un carril horizontal: a diferencia de
 * `useRailScroll` (que solo necesita saber si se está en un extremo, para
 * ocultar flechas), esto requiere la proporción exacta del contenido visible
 * y su posición de scroll.
 *
 * Promovido a `src/app/lib/hooks/` (Constitución, Principio II — "promover
 * solo con reuso genuino, no antes") al aparecer un tercer consumidor real
 * (`RecintoRelatedVenuesCarousel`, `_components/RecintoRelatedVenues/` de la
 * página de recinto): antes vivía colocado en el módulo CMS «Productos»
 * (`ProductCarousel`), con una copia privada duplicada en «ProductosLocalizados»
 * (`RankingCarousel`) porque importarlo directamente habría acoplado dos
 * módulos CMS entre sí — la propia copia dejaba anotado "candidata a
 * promoción... si aparece un tercer consumidor real", que es este caso.
 *
 * Compone sobre el mismo `railRef` que expone `useRailScroll` — no vuelve a
 * medir el paso de scroll, solo deriva la proporción visible/posición a
 * partir de `scrollWidth`/`clientWidth`/`scrollLeft` del mismo contenedor.
 */
export function useCarouselProgress(
  railRef: RefObject<HTMLDivElement | null>,
  itemsLength: number,
): UseCarouselProgressResult {
  const [hasOverflow, setHasOverflow] = useState(false)
  const [thumbWidth, setThumbWidth] = useState(100)
  const [thumbLeft, setThumbLeft] = useState(0)

  const recalc = useCallback(() => {
    const el = railRef.current
    if (!el) return

    const max = el.scrollWidth - el.clientWidth
    const overflow = max > 1
    setHasOverflow(overflow)

    if (!overflow) {
      setThumbWidth(100)
      setThumbLeft(0)
      return
    }

    const ratio = el.clientWidth / el.scrollWidth
    const progress = Math.min(1, Math.max(0, el.scrollLeft / max))
    setThumbWidth(ratio * 100)
    setThumbLeft(progress * (100 - ratio * 100))
  }, [railRef])

  useEffect(() => {
    recalc()
    const el = railRef.current
    if (!el) return

    const resizeObserver = new ResizeObserver(recalc)
    resizeObserver.observe(el)
    window.addEventListener('resize', recalc)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', recalc)
    }
  }, [recalc, itemsLength, railRef])

  return { hasOverflow, thumbWidth, thumbLeft, recalc }
}
